import { Component, computed, input, inject, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { TelemetryEntry } from "../../../models/shared/telemetry";
import { CommonUtilsService } from "../../../services/utils/common-utils/common-utils.service";

@Component({
  standalone: true,
  selector: "app-temperature-display",
  imports: [TranslateModule, ProgressSpinnerModule, TimeoutComponent],
  templateUrl: "./temperature-display.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./temperature-display.component.scss",
})
export class TemperatureDisplayComponent {
  public insideTemperature = input<TelemetryEntry | null | undefined>();
  public outsideTemperature = input<TelemetryEntry | null | undefined>();
  private commonUtils = inject(CommonUtilsService);

  public delta = computed(() => {
    const insideTemperature = this.insideTemperature();
    const outsideTemperature = this.outsideTemperature();

    // If either temperature is missing or not current, return null
    if (
      !insideTemperature ||
      !this.commonUtils.isTimestampFromToday(insideTemperature.timestamp) ||
      !outsideTemperature ||
      !this.commonUtils.isTimestampFromToday(outsideTemperature.timestamp)
    ) {
      return null;
    }
    return this.getDelta(outsideTemperature?.value, insideTemperature?.value);
  });

  public outsideTemperatureDisplay = computed(() => {
    const outsideTemperature = this.outsideTemperature();
    if (outsideTemperature) {
      return this.commonUtils.isTimestampFromToday(outsideTemperature.timestamp) ? outsideTemperature.value : "-";
    } else {
      return "-";
    }
  });

  public insideTemperatureDisplay = computed(() => {
    const insideTemperature = this.insideTemperature();
    if (insideTemperature) {
      return this.commonUtils.isTimestampFromToday(insideTemperature.timestamp) ? insideTemperature.value : "-";
    } else {
      return "-";
    }
  });

  public loading = computed(() => {
    const insideTemperature = this.insideTemperature();
    const outsideTemperature = this.outsideTemperature();
    return insideTemperature === null && outsideTemperature === null;
  });

  public noData = computed(() => {
    const insideTemperature = this.insideTemperature();
    const outsideTemperature = this.outsideTemperature();
    return insideTemperature === undefined && outsideTemperature === undefined;
  });
  public timeOut = createTimeoutSignal(this.loading);

  /**
   * Calculates the difference between two temperature values.
   * @param a - The first temperature value, can be null or undefined.
   * @param b - The second temperature value, can be null or undefined.
   * @returns The difference (b - a) if both values are defined and not null; otherwise, null.
   */
  private getDelta(a: number | null | undefined, b: number | null | undefined) {
    if (a !== undefined && a !== null && b !== undefined && b !== null) {
      return b - a;
    } else {
      return null;
    }
  }
}
